import { useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { indentWithTab } from '@codemirror/commands';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';
import { useHotkeys } from 'react-hotkeys-hook';
import PropTypes from 'prop-types';

import './styles.scss';
import {
  selectContent,
  selectInsertedOperationCode,
  selectInsertLocation,
  selectUpdateLocalContent,
} from '../../store/CodePanel/selectors';
import {
  updateContent,
  saveContentToSession,
  insertOperationCode,
} from '../../store/CodePanel/actions';
import { sendGAEvent } from '../../store/GoogleAnalytics/actions';
import { operationItemDataType } from '../../constants';
import { selectDarkToggle } from '../../store/NavigationBar/selectors';
import { setReload } from '../../store/PreviewPanel/actions';
import ButtonTooltip from '../Tooltip/buttonTooltip';
import isMacOs from '../../utils/getOperatingSystem';
import {
  RELOAD_HOT_KEY_MACOS,
  RELOAD_HOT_KEY_WINDOWS_OR_LINUX,
  RUN_CODE_HOT_KEY_CODEMIRROR_MAC,
  RUN_CODE_HOT_KEY_CODEMIRROR_WINDOWS_OR_LINUX,
} from '../../constants/hotkeys';
import HtmlFormatter from '../../utils/htmlFormatter';
import {
  CLICK_CLEAR_CODE_BUTTON,
  CLICK_COPY_CODE_BUTTON,
  CLICK_FORMAT_BUTTON,
  CLICK_RUN_CODE_BUTTON,
  USE_RUN_CODE_SHORTCUT,
} from '../../constants/google-analytics';

// Component containing the code editor
const CodePanel = ({
  propsContent,
  isDarkTheme,
  onUpdateContent,
  onSaveContentToSession,
  onSetReload,
  updateLocalContent,
  insertedOperationCode,
  insertLocation,
  onInsertOperationCode,
  onSendGAEvent,
}) => {
  // Reference to the editor dom element
  const editor = useRef();
  // Reference to the EditorView of editor dom element
  const view = useRef();
  // The htmlformatter object
  const htmlFormatter = new HtmlFormatter();

  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Saves code to local storage. Has different behavior depending on event type.
  const saveCode = (event = null) => {
    if (view?.current) {
      const codeToSave = view.current?.state?.doc?.toString();
      if (event && event.type) {
        switch (event.type) {
          case 'visibilitychange':
            if (document.visibilityState === 'hidden') onSaveContentToSession(codeToSave);
            break;
          case 'pagehide':
            onSaveContentToSession(codeToSave);
            break;
          default:
            break;
        }
      } else {
        onSaveContentToSession(codeToSave);
      }
    }
  };
  // onReload event handler for 'ctrl+s/cmd+s'
  const onReload = (event) => {
    // prevent the default browser's save behavior
    if (event) {
      event.preventDefault();
    }
    onSendGAEvent(USE_RUN_CODE_SHORTCUT);
    if (propsContent === view.current?.state.doc.toString()) {
      onSetReload(true);
    } else {
      onUpdateContent(view.current?.state.doc.toString(), true);
      saveCode();
    }
  };
  const reloadHotKey = isMacOs() ? RELOAD_HOT_KEY_MACOS : RELOAD_HOT_KEY_WINDOWS_OR_LINUX;
  // added reload hotkey
  // `onRunCode` won't work here because we need to call `event.preventDefault()`.
  useHotkeys(reloadHotKey, onReload);

  // The onclick handler of "Run code" button.
  const onRunCode = () => {
    onSendGAEvent(CLICK_RUN_CODE_BUTTON);
    if (propsContent === view.current?.state.doc.toString()) {
      onSetReload(true);
    } else {
      onUpdateContent(view.current?.state.doc.toString(), true);
      saveCode();
    }
  };

  // The onclick handler of "Copy all" button
  const onCopyAll = () => {
    onSendGAEvent(CLICK_COPY_CODE_BUTTON);
    navigator.clipboard.writeText(view.current?.state.doc.toString());
  };

  // The onclick handler of "Clear code" button
  const onClearCode = () => {
    onSendGAEvent(CLICK_CLEAR_CODE_BUTTON);
    view.current?.dispatch({
      changes: { from: 0, to: view.current.state.doc.length, insert: '' },
    });
  };

  // Scroll to the selection in codemirror 6 if it isn't entirely in view already.
  // For cases where the selection is bigger than the viewable range it will scroll to start of selection.
  const scrollToSelection = () => {
    if (view.current) {
      // topPos will be the top of selection. Where we will want to scroll to
      let topPos = 0;
      // botPos will be the bottom of selection. How we check if selection is entirely in view.
      let botPos = 0;
      let heightOfLine = 0;
      const currentSelectionAnchor = view.current.state.selection?.ranges[0]?.anchor;
      const currentSelectionHead = view.current.state.selection?.ranges[0]?.head;
      // Try seeing if the anchor and head is > 0. If not skip this if statement because it will always scroll to top anyways.
      if (
        currentSelectionAnchor &&
        currentSelectionHead &&
        currentSelectionAnchor > 0 &&
        currentSelectionHead > 0
      ) {
        // The end of selection is the higher of the 2 positions, head and anchor.
        // We will use this to check if the bottomost line of selection will be in view.
        // If not we will scroll.
        const currentSelectionEnd = Math.max(currentSelectionAnchor, currentSelectionHead);
        const currentSelectionStart = Math.min(currentSelectionAnchor, currentSelectionHead);
        const selectedLastLine = view.current.lineBlockAt(currentSelectionEnd);
        const selectedFirstLine = view.current.lineBlockAt(currentSelectionStart);
        heightOfLine = selectedLastLine?.height || 0;
        botPos = selectedLastLine?.top || 0;
        topPos = selectedFirstLine?.top || 0;
      }
      // In the case where the anchor and head don't exist or is 0. It will always scroll to topmost position if not in view already.
      const { scrollDOM } = view.current;
      const scrollDiff = botPos - scrollDOM.scrollTop;
      // If the scrollDiff is greater than the clientHeight - heightOfLine or negative it means the
      // selection isn't in view. We only scroll if the line isn't in view.
      if (scrollDOM && (scrollDiff > scrollDOM.clientHeight - heightOfLine || scrollDiff < 0)) {
        scrollDOM.scrollTo({
          top: topPos,
          behavior: 'smooth',
        });
      }
    }
  };

  // Formats the code in the editor using prettier
  const formatDocument = () => {
    if (view.current) {
      try {
        const currentContent = view.current.state.doc.toString();
        // Only if the content is not formatted, start formatting
        if (!htmlFormatter.formatCheck(currentContent)) {
          // The range is the current selection object {anchor, head, ...}
          const currentSelectionRange = view.current.state.selection.ranges[0];
          // Result = {formattedText, selection}
          const result = htmlFormatter.formatWithSelection(currentContent, {
            head: currentSelectionRange.head,
            anchor: currentSelectionRange.anchor,
          });
          view.current.dispatch({
            changes: {
              from: 0,
              to: currentContent.length,
              insert: result.formattedText,
            },
            selection: {
              anchor: result.selection.anchor || 0,
              head: result.selection.head || 0,
            },
          });
          scrollToSelection();
        }
        view.current.focus();
      } catch (e) {
        // Do nothing
        console.log(e.message);
      }
    }
  };
  // Generate keymap with the given input for windows, mac, and action
  const generateKeyMap = (windowsInput, macInput, action) => {
    return keymap.of([
      {
        key: windowsInput,
        mac: macInput,
        preventDefault: true,
        run() {
          action();
        },
      },
    ]);
  };

  // Extensions used in code mirror
  const lightModeExtensions = [
    basicSetup,
    html(),
    // Tab size of 2 to match with prettier tab sizes
    EditorState.tabSize.of(2),
    // Enable tabbing
    keymap.of([indentWithTab]),
    // custom keymaps
    // We still need to keep this because code mirror blocks our global keyevent listener.
    // So is iframe. That's why the hotkey won't work in iframe.
    generateKeyMap(
      RUN_CODE_HOT_KEY_CODEMIRROR_WINDOWS_OR_LINUX,
      RUN_CODE_HOT_KEY_CODEMIRROR_MAC,
      onReload,
    ),
  ];
  const darkModeExtensions = [
    ...lightModeExtensions,
    // Dark theme
    oneDark,
  ];
  // The current applied extensions in code mirror
  const [currentExtensions, setCurrentExtensions] = useState(lightModeExtensions);

  // This effect updates the code editor doc based on the redux store
  // we only change the store when:
  // 1) loading in the localStorage code,
  // 2) saving the current code editor doc(which is same as propsContent in this case)
  useEffect(() => {
    if (updateLocalContent) {
      // If the local content state, view.current.state.doc.toString, equals the props we are passing in we return without doing anything
      if (view.current && view.current?.state.doc.toString() === propsContent) return;
      let formattedPropsContent;
      try {
        // Check if propsContent is formatted already
        if (htmlFormatter.formatCheck(propsContent)) {
          formattedPropsContent = propsContent;
        } else {
          formattedPropsContent = htmlFormatter.format(propsContent);
        }
      } catch (e) {
        formattedPropsContent = propsContent;
        console.log(e.message);
      }
      if (view.current) {
        view.current.dispatch({
          changes: { from: 0, to: view.current.state.doc.length, insert: formattedPropsContent },
        });
      } else {
        const state = EditorState.create({
          doc: formattedPropsContent,
          extensions: currentExtensions,
        });
        view.current = new EditorView({ state, parent: editor.current });
      }
      // Keep content the same in redux just set updateLocalContent to false
      // This helps start over popup change the local changes you made without saving to redux store if propsContent is same
      onUpdateContent(propsContent, false);
      // In this instance it should scroll back to first line since selection is reset to first line
      scrollToSelection();
      saveCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `currentExtensions`, `htmlFormatter`, `onUpdateContent` and `saveCode` should not be passed into the dependencies array
  }, [propsContent, updateLocalContent]);

  // This effect handles theme changes in code mirror 6 related to dark mode toggle
  useEffect(() => {
    setCurrentExtensions(isDarkTheme ? darkModeExtensions : lightModeExtensions);
    if (view.current) {
      view.current.setState(
        EditorState.create({
          doc: view.current.state.doc || '',
          selection: view.current.state.selection,
          extensions: isDarkTheme ? darkModeExtensions : lightModeExtensions,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `darkModeExtensions` and `lightModeExtensions` should not be passed into the dependencies array
  }, [isDarkTheme]);

  // This effect handles inserting the operation item code
  useEffect(() => {
    if (view.current && insertedOperationCode) {
      const currentContent = view.current.state.doc.toString();
      // The insert location is either the method or property location
      const firstIndexOfInsertLocation = currentContent.indexOf(insertLocation, 0);
      if (firstIndexOfInsertLocation !== -1) {
        const codeToInsert = `${insertedOperationCode}\n\n`;
        view.current.dispatch({
          changes: { from: firstIndexOfInsertLocation, insert: codeToInsert },
          selection: {
            anchor: firstIndexOfInsertLocation,
            head: firstIndexOfInsertLocation + insertedOperationCode.length,
          }, // anchor is start, head is end
        });
        formatDocument();
        view.current.focus();
      }
      // Set insertedOperationCode back to empty string so if they click on same method it will call this effect again
      onInsertOperationCode('', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `formatDocument` and `onInsertOperationCode` should not be passed into the dependencies array
  }, [insertedOperationCode, insertLocation]);

  // This effect handles saving the code to local storage
  useEffect(() => {
    const state = EditorState.create({
      doc: '',
      extensions: currentExtensions,
    });
    view.current = new EditorView({ state, parent: editor.current });
    // Save code when switching tabs, or closing tab. Visibilitychange works on mobile too unlike pagehide/onunload.
    document.addEventListener('visibilitychange', saveCode);
    document.addEventListener('pagehide', saveCode);
    return () => {
      document.removeEventListener('visibilitychange', saveCode);
      document.removeEventListener('pagehide', saveCode);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `currentExtensions` and `saveCode` should not be passed into the dependencies array
  }, []);

  // Handles the dragOver behavior for operation item
  const onDragOver = (e) => {
    if (view.current) {
      const curPos = view.current.posAtCoords({ x: e.pageX, y: e.pageY });
      view.current.dispatch({ selection: { anchor: curPos } });
    }
    setIsDraggingOver(true);
    e.preventDefault();
    e.stopPropagation();
  };

  // Handles the dragLeave behavior for operation item
  const onDragLeave = () => {
    setIsDraggingOver(false);
  };

  // Handles the drop behavior of operation item
  const onDrop = (e) => {
    setIsDraggingOver(false);
    const code = e.dataTransfer.getData(operationItemDataType);
    if (code && view.current) {
      const curPos = view.current.posAtCoords({ x: e.pageX, y: e.pageY });
      const curLine = view.current.state.doc.lineAt(curPos); // Start of line
      const codeToInsert = `${code}\n\n`;
      // Insert code at start of current line and select the code
      view.current.dispatch({
        changes: { from: curLine.from, insert: codeToInsert },
        selection: { anchor: curLine.from, head: curLine.from + code.length },
      });

      // Can improve this to so it doesnt change whole doc
      formatDocument();
      // Focus on the editor when dropping the code
      view.current.focus();
    }
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className={isDarkTheme ? 'code-mirror card-shadow dark' : 'code-mirror card-shadow'}>
      <div className="panel-header card-header">
        <span className="title">Editor</span>
        <div className="buttons">
          <button
            id="button__run__tooltip"
            className="button__run"
            onClick={onRunCode}
            type="button"
          >
            <span className="material-icons">play_arrow</span>
            <p>Run code</p>
          </button>
          <ButtonTooltip
            anchorId="button__run__tooltip"
            content={<div className="tooltip-text">{isMacOs() ? 'Command(⌘)+s' : 'Ctrl+s'}</div>}
          />
          <button id="button__copy__tooltip" onClick={onCopyAll} type="button">
            <span className="material-icons">copy_all</span>
          </button>
          <ButtonTooltip
            anchorId="button__copy__tooltip"
            content={<div className="tooltip-text">Copy code</div>}
          />
          <button id="button__clear__tooltip" onClick={onClearCode} type="button">
            <span className="material-icons">delete</span>
          </button>
          <ButtonTooltip
            anchorId="button__clear__tooltip"
            content={<div className="tooltip-text">Clear code</div>}
          />
          <button
            id="button__format__tooltip"
            onClick={() => {
              onSendGAEvent(CLICK_FORMAT_BUTTON);
              formatDocument();
            }}
            type="button"
          >
            <span className="material-icons">format_align_left</span>
          </button>
          <ButtonTooltip
            anchorId="button__format__tooltip"
            content={<div className="tooltip-text">Format</div>}
          />
        </div>
      </div>
      <div
        className={isDraggingOver ? 'code-mirror-container dragging' : 'code-mirror-container'}
        ref={editor}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      />
    </div>
  );
};

CodePanel.propTypes = {
  propsContent: PropTypes.string.isRequired,
  isDarkTheme: PropTypes.bool.isRequired,
  updateLocalContent: PropTypes.bool.isRequired,
  insertedOperationCode: PropTypes.string.isRequired,
  insertLocation: PropTypes.string.isRequired,
  onUpdateContent: PropTypes.func.isRequired,
  onSaveContentToSession: PropTypes.func.isRequired,
  onSetReload: PropTypes.func.isRequired,
  onInsertOperationCode: PropTypes.func.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    propsContent: selectContent(state),
    isDarkTheme: selectDarkToggle(state),
    updateLocalContent: selectUpdateLocalContent(state),
    insertedOperationCode: selectInsertedOperationCode(state),
    insertLocation: selectInsertLocation(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onUpdateContent: updateContent,
      onSaveContentToSession: saveContentToSession,
      onSetReload: setReload,
      onInsertOperationCode: insertOperationCode,
      onSendGAEvent: sendGAEvent,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(CodePanel);
